import axios from 'axios';
import { DateTime } from 'luxon';
import { IUpdateExpirationDateDTO } from '../types/update-expiration-date.dto';
import { applyTimezoneOffset } from '@/common/timezone/timezone.helper';
import { IRecipient } from '@lib-shared/types/Recipient';
import { apolloClient } from '@/apollo';
import { DocumentMutations } from '@/api/documents/graphql/document.mutations';
import { ISentEmail } from '@/document-actions/types/sent-email';
import { IRecallDocumentDTO } from '@/api/document-actions/types/recall-document.dto';

export class DocumentActionService {
  private static readonly documentIdName = 'document-id';
  private static readonly endpoint = '/save/board';

  static async updateExpirationDate({
    expirationDate,
    email,
    emailSubject,
    emailMessage,
    documentId,
  }: IUpdateExpirationDateDTO) {
    const date = DateTime.fromISO(expirationDate).toUTC();
    const dateUtc = DateTime.fromISO(applyTimezoneOffset(expirationDate, false)).toUTC();
    const body = new FormData();
    body.append('document-expiration-date', date.toFormat('yyyy-LL-dd'));
    body.append('document-expiration-time', date.toFormat('HH:mm'));
    body.append('document-expiration-utc', dateUtc.toFormat('yyyy-LL-dd HH:mm'));
    body.append('document-expiration-email', email ? '1' : '0');
    body.append('document-expiration-email-subject', emailSubject);
    body.append('document-expiration-email-message', emailMessage);
    body.append('action', 'expiration');
    body.append(DocumentActionService.documentIdName, documentId);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data;
  }

  static sendEmailReminder = async (payload: {
    documentId: string;
    isNotSignedSelected: boolean;
    isNotOpenedSelected: boolean;
  }): Promise<boolean> => {
    const {
      data: { sendEmailReminder },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.sendEmailReminderMutation,
      variables: {
        documentId: payload.documentId,
        isNotSignedSelected: payload.isNotSignedSelected,
        isNotOpenedSelected: payload.isNotOpenedSelected,
      },
    });
    return sendEmailReminder;
  };

  static sendSMSReminder = async (
    documentId: string,
    message: string,
    isNotSignedSelected: boolean,
    isNotOpenedSelected: boolean
  ): Promise<boolean> => {
    const {
      data: { sendSMSReminder },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.sendSMSReminderMutation,
      variables: { documentId, message, isNotSignedSelected, isNotOpenedSelected },
    });
    return sendSMSReminder;
  };

  static async updateContactNumber(
    documentId: string,
    id: string,
    firstName: string,
    lastName: string,
    mobile: string
  ): Promise<IRecipient> {
    const body = new FormData();
    body.append('contact-first_name', firstName);
    body.append('contact-last_name', lastName);
    body.append('contact-mobile', mobile);
    body.append(DocumentActionService.documentIdName, documentId);
    body.append('contact-id', id);
    body.append('action', 'save');

    const { data } = await axios.post('/save/contact', body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data.error);
    }
    return data;
  }

  static async cancel(documentId: string): Promise<boolean> {
    const body = new FormData();

    body.append('action', 'cancel');
    body.append(DocumentActionService.documentIdName, documentId);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return !!data.status;
  }

  static async createNewVersion(payload: {
    isDocumentContentChecked: boolean;
    isRecipientsChecked: boolean;
    isEmailContentChecked: boolean;
    isRecallChecked: boolean;
    documentId: string;
  }): Promise<string> {
    const body = new FormData();
    body.append('document-keep-content', (+payload.isDocumentContentChecked).toString());
    body.append('document-keep-recipients', (+payload.isRecipientsChecked).toString());
    body.append('document-keep-email', (+payload.isEmailContentChecked).toString());
    body.append('document-recall-previous', (+payload.isRecallChecked).toString());

    body.append('action', 'newversion');
    body.append(DocumentActionService.documentIdName, payload.documentId);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1 || !data.document_id) {
      throw new Error(data);
    }
    return data.document_id;
  }

  static async saveUploadDocument(uploadFile: File): Promise<string> {
    const body = new FormData();
    body.append('file', uploadFile);
    const { data } = await axios.post('/save/upload', body);
    if (!data.success) {
      throw new Error(data);
    }
    return data.filename;
  }

  static async uploadSigned(payload: {
    documentId: string;
    uploadFileList: string;
  }): Promise<string> {
    const body = new FormData();
    body.append('action', 'uploadsigned');
    body.append(DocumentActionService.documentIdName, payload.documentId);
    body.append('upload-file-list', payload.uploadFileList);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }

  static async uploadSignedWithoutVerification(payload: { documentId: string }): Promise<string> {
    const body = new FormData();
    body.append('action', 'signwithoutverification');
    body.append(DocumentActionService.documentIdName, payload.documentId);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }

  static async updateSchedule({
    sendingDate,
    documentId,
  }: {
    sendingDate: string;
    documentId: string;
  }) {
    const date = DateTime.fromISO(applyTimezoneOffset(sendingDate, true)).toUTC();
    const dateUtc = DateTime.fromISO(sendingDate).toUTC();
    const body = new FormData();
    body.append('document-scheduled_sending_date', date.toFormat('yyyy-LL-dd'));
    body.append('document-scheduled_sending_time', date.toFormat('HH:mm'));
    body.append('document-scheduled_sending_utc', dateUtc.toFormat('yyyy-LL-dd HH:mm'));
    body.append('action', 'reschedule');
    body.append(DocumentActionService.documentIdName, documentId);
    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }

  static async sendSMSToRecipient({
    documentId,
    recipient,
    message,
  }: {
    documentId: string;
    recipient: IRecipient;
    message: string;
  }) {
    const body = new FormData();
    body.append('action', 'recipientsms');
    body.append('recipient-id', recipient.id);
    body.append('document-sms-recipient-mobile', recipient.mobile);
    body.append('document-sms-recipient-message', message);
    body.append(DocumentActionService.documentIdName, documentId);

    const { data } = await axios.post(this.endpoint, body);

    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }

  static async sendVideoReminder({
    documentId,
    videoId,
    reminderText,
  }: {
    documentId: string;
    videoId: string;
    reminderText: string;
  }) {
    const body = new FormData();
    body.append('action', 'remindervideo');
    body.append(DocumentActionService.documentIdName, documentId);
    body.append('video-id', videoId);
    body.append('reminder-text', reminderText);

    const { data } = await axios.post(this.endpoint, body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }
  static async sentEmail(documentId: string): Promise<ISentEmail> {
    const statusFail = 0;
    const {
      data: { email, recipient, code, error, status },
    } = await axios.get(`/get/document/${documentId}/email-sent`);
    if (error && status === statusFail) {
      throw new Error(error);
    }
    return { ...email, recipient, code };
  }

  static async recall({
    documentId,
    emailSubject,
    emailMessage,
  }: IRecallDocumentDTO): Promise<boolean> {
    const body = new FormData();

    body.append('document-id', documentId);
    body.append('action', 'recall');

    if (emailSubject && emailMessage) {
      body.append('document-recall-email', 'true');
      body.append('document-recall-email-subject', emailSubject);
      body.append('document-recall-email-message', emailMessage);
    }

    const { data } = await axios.post(this.endpoint, body);

    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== true) {
      throw new Error(data.error);
    }
    return data;
  }

  static async markAsLost(documentId: string) {
    const body = new FormData();
    body.append('action', 'lost');
    body.append(DocumentActionService.documentIdName, documentId);

    const { data } = await axios.post(this.endpoint, body);

    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.status;
  }
}
